/**
 * Derives the "Parallax Preview" model from the session events of the observe
 * pilot (B2C_PARALLAX_MODE=observe): the orchestrator publishes
 * `director.parallax_observed` (the shots it WOULD have pinned — the delivered
 * echo is untouched) and the composer publishes
 * `composition.parallax_preview.completed` after rendering the standalone
 * preview clips post-delivery. Pure module (no React) so the join is unit-testable;
 * the latest event of each type wins (want-changes regenerates republish).
 */

import type { EchoflicksSessionEvent } from "../types";

export type ParallaxClipStatus = "rendered" | "failed" | "timeout" | "pending";

export type ParallaxPreviewPick = {
  file: string;
  move: string;
  kind: string;
  anchor: string;
  background: string;
  quality: number | null;
  clipKey: string | null;
  clipStatus: ParallaxClipStatus;
  clipError: string | null;
};

export type ParallaxConsideredRow = {
  file: string;
  quality: number | null;
  kind: string;
  verdict: string;
};

export type ParallaxPreviewModel = {
  observedAt: string | null;
  source: string | null;
  picks: ParallaxPreviewPick[];
  considered: ParallaxConsideredRow[];
  renderOutcome: string | null;
  renderedCount: number;
  storageBucket: string | null;
};

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const num = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const payloadOf = (event: EchoflicksSessionEvent): Record<string, unknown> =>
  event.raw_payload && typeof event.raw_payload === "object"
    ? (event.raw_payload as Record<string, unknown>)
    : {};

const latestOfType = (
  events: EchoflicksSessionEvent[],
  eventType: string,
): EchoflicksSessionEvent | null => {
  let latest: { event: EchoflicksSessionEvent; index: number } | null = null;
  events.forEach((event, index) => {
    if (event.event_type !== eventType) return;
    if (!latest) {
      latest = { event, index };
      return;
    }
    const leftTime = Date.parse(latest.event.occurred_at ?? "");
    const rightTime = Date.parse(event.occurred_at ?? "");
    if (
      Number.isFinite(leftTime) &&
      Number.isFinite(rightTime) &&
      leftTime !== rightTime
    ) {
      if (rightTime > leftTime) latest = { event, index };
      return;
    }
    if (index > latest.index) latest = { event, index };
  });
  return latest ? (latest as { event: EchoflicksSessionEvent }).event : null;
};

type ClipResult = {
  key: string | null;
  status: ParallaxClipStatus;
  error: string | null;
};

export const parallaxPreviewFromEvents = (
  events: EchoflicksSessionEvent[],
): ParallaxPreviewModel | null => {
  const observed = latestOfType(events, "director.parallax_observed");
  if (!observed) return null;
  const observedPayload = payloadOf(observed);
  const plannedRaw = Array.isArray(observedPayload.planned)
    ? observedPayload.planned
    : [];
  if (plannedRaw.length === 0) return null;

  const completed = latestOfType(
    events,
    "composition.parallax_preview.completed",
  );
  const completedPayload = completed ? payloadOf(completed) : null;
  const clipsByFile = new Map<string, ClipResult>();
  if (completedPayload && Array.isArray(completedPayload.entries)) {
    for (const raw of completedPayload.entries) {
      if (!raw || typeof raw !== "object") continue;
      const entry = raw as Record<string, unknown>;
      const file = text(entry.file);
      if (!file || clipsByFile.has(file)) continue;
      const key = text(entry.s3_key) || null;
      const status = text(entry.status);
      clipsByFile.set(file, {
        key,
        status:
          status === "rendered" && key
            ? "rendered"
            : status === "timeout"
              ? "timeout"
              : "failed",
        error: text(entry.error) || null,
      });
    }
  }

  const picks: ParallaxPreviewPick[] = [];
  for (const raw of plannedRaw) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Record<string, unknown>;
    const file = text(entry.file);
    if (!file) continue;
    const clip = clipsByFile.get(file);
    picks.push({
      file,
      move: text(entry.move),
      kind: text(entry.kind),
      anchor: text(entry.anchor),
      background: text(entry.background),
      quality: num(entry.quality),
      clipKey: clip?.key ?? null,
      clipStatus: clip ? clip.status : "pending",
      clipError: clip?.error ?? null,
    });
  }
  if (picks.length === 0) return null;

  const considered: ParallaxConsideredRow[] = [];
  if (Array.isArray(observedPayload.considered)) {
    for (const raw of observedPayload.considered) {
      if (!raw || typeof raw !== "object") continue;
      const entry = raw as Record<string, unknown>;
      const file = text(entry.file);
      if (!file) continue;
      considered.push({
        file,
        quality: num(entry.quality),
        kind: text(entry.kind),
        verdict: text(entry.verdict),
      });
    }
  }

  return {
    observedAt: observed.occurred_at ?? null,
    source: text(observedPayload.source) || null,
    picks,
    considered,
    renderOutcome: completedPayload
      ? text(completedPayload.outcome) || null
      : null,
    renderedCount: picks.filter((pick) => pick.clipStatus === "rendered")
      .length,
    storageBucket: completedPayload
      ? text(completedPayload.storage_bucket) || null
      : null,
  };
};
